// ~/components/ToursLightbox.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GatsbyImage } from "gatsby-plugin-image";

/**
 * ToursLightbox — white backdrop, controls outside image
 * Props:
 *  - images: same shape as gallery's images array
 *  - startIndex: number
 *  - onClose: () => void
 */
export default function ToursLightbox({
	images = [],
	startIndex = 0,
	onClose,
}) {
	const count = images?.length ?? 0;
	const safeStart = Math.min(
		Math.max(0, startIndex || 0),
		Math.max(0, count - 1)
	);
	const [index, setIndex] = useState(safeStart);

	const prev = useCallback(
		() => setIndex((i) => (i - 1 + count) % count),
		[count]
	);
	const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

	// supports old + new shapes
	const pickImageData = useCallback((node) => {
		const cs = node?.localFile?.childImageSharp;
		return cs?.hero || cs?.tile || cs?.gatsbyImageData || null;
	}, []);
	const imageData = useMemo(
		() => (count ? pickImageData(images[index]) : null),
		[images, index, count, pickImageData]
	);

	// keyboard controls
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape") onClose?.();
			if (e.key === "ArrowLeft") prev();
			if (e.key === "ArrowRight") next();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [next, prev, onClose]);

	// lock body scroll while open
	useEffect(() => {
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prevOverflow;
		};
	}, []);

	if (!count || !imageData) return null;

	return (
		<div
			className="fixed inset-0 z-40 bg-white"
			role="dialog"
			aria-modal="true"
			style={{ height: "100dvh" }}
			onClick={onClose}
		>
			{/* Inset margins: larger left/right on lg/xl */}
			<div
				className="
          grid grid-rows-[auto,1fr,auto] gap-y-3 md:gap-y-4
          h-full w-full max-w-screen-2xl mx-auto
          p-4 md:p-6 lg:py-8 lg:px-16 xl:py-10 xl:px-24
        "
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header (Close) */}
				<div className="h-14 flex items-center justify-end">
					<button
						onClick={onClose}
						aria-label="Close"
						className="grid place-items-center w-11 h-11 rounded-full
                       bg-black/5 hover:bg-black/10 border border-black/10
                       text-black focus:outline-none focus-visible:ring"
					>
						<svg
							viewBox="0 0 24 24"
							width="20"
							height="20"
							stroke="currentColor"
							strokeWidth="2"
							fill="none"
							aria-hidden="true"
						>
							<line x1="6" y1="6" x2="18" y2="18" />
							<line x1="6" y1="18" x2="18" y2="6" />
						</svg>
					</button>
				</div>

				{/* Image area (centered) — extra horizontal room via px + max-width clamps */}
				<div className="min-h-0 flex items-center justify-center px-4 md:px-6 lg:px-12 xl:px-16">
					<div
						className="
              w-full h-full mx-auto rounded-xl md:rounded-2xl overflow-hidden ring-1 ring-black/5
              max-w-full
              lg:max-w-[70vw]
              xl:max-w-[60vw]
              2xl:max-w-[56vw]
            "
					>
						<GatsbyImage
							image={imageData}
							alt={images[index]?.alternativeText || "Tour photo enlarged"}
							className="w-full h-full"
							imgClassName="w-full h-full object-contain"
							loading="eager"
						/>
					</div>
				</div>

				{/* Footer (Prev / Counter / Next) */}
				<div className="h-16">
					<div className="grid grid-cols-3 items-center h-full">
						<div className="justify-self-start">
							{count > 1 && (
								<button
									onClick={prev}
									aria-label="Previous image"
									className="grid place-items-center w-12 h-12 rounded-full
                             bg-black/5 hover:bg-black/10 border border-black/10
                             text-black focus:outline-none focus-visible:ring"
								>
									<svg
										viewBox="0 0 24 24"
										width="22"
										height="22"
										stroke="currentColor"
										strokeWidth="2"
										fill="none"
										aria-hidden="true"
									>
										<polyline points="15 18 9 12 15 6" />
									</svg>
								</button>
							)}
						</div>

						<div className="justify-self-center text-sm md:text-base text-gray-600 select-none">
							{index + 1} / {count}
						</div>

						<div className="justify-self-end">
							{count > 1 && (
								<button
									onClick={next}
									aria-label="Next image"
									className="grid place-items-center w-12 h-12 rounded-full
                             bg-black/5 hover:bg-black/10 border border-black/10
                             text-black focus:outline-none focus-visible:ring"
								>
									<svg
										viewBox="0 0 24 24"
										width="22"
										height="22"
										stroke="currentColor"
										strokeWidth="2"
										fill="none"
										aria-hidden="true"
									>
										<polyline points="9 18 15 12 9 6" />
									</svg>
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
